import { useEffect, useRef, useState } from 'react';

interface Props {
  /**
   * Called when the user clicks "Reveal stored value". Should hit the
   * server's reveal endpoint and return the plaintext (or null if
   * nothing's stored). Errors propagate as thrown exceptions and
   * surface inline.
   */
  fetcher: () => Promise<string | null>;
  /**
   * Called with the decrypted value once `fetcher` resolves. The
   * parent typically pipes it into its input state so the value
   * shows in the actual field rather than in a separate display.
   */
  onReveal: (value: string) => void;
  /**
   * Called when the revealed value should be cleared — either the
   * user clicked "Hide" or the auto-hide timer fired. The parent
   * typically resets its input state to empty so the
   * "saved — leave blank to keep" placeholder reappears.
   */
  onHide: () => void;
  /**
   * How long the revealed value stays in the parent's input before
   * the component forces `onHide()`. Defaults to 30 seconds — long
   * enough to read or copy, short enough that walking-away-from-the-
   * laptop doesn't leave the secret on screen indefinitely.
   */
  autoHideMs?: number;
  /** Optional label that replaces the default "stored value" wording. */
  label?: string;
}

/**
 * Companion to a "saved — leave blank to keep" secret input. Renders
 * a small bordered button that, on click, fetches the decrypted
 * value from the server and asks the parent to display it inside the
 * input itself via `onReveal`. Toggles to a "Hide" button while a
 * value is on screen, with a 30-second auto-hide as a backstop in
 * case the admin walks away.
 *
 * The reveal click is a deliberate "I want to expose this" gesture —
 * the value is never returned by the regular settings GETs. Once
 * cleared, the input goes back to its empty + placeholder state and
 * the existing "blank-on-submit means keep" save semantics apply.
 */
export function RevealStoredValue({
  fetcher,
  onReveal,
  onHide,
  autoHideMs = 30_000,
  label = 'stored value',
}: Props) {
  const [state, setState] = useState<'idle' | 'loading' | 'revealed' | 'empty' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const startAutoHide = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      onHide();
      setState('idle');
    }, autoHideMs);
  };

  const reveal = async () => {
    setState('loading');
    setErrorMsg('');
    try {
      const v = await fetcher();
      if (v === null || v === '') {
        setState('empty');
        return;
      }
      onReveal(v);
      setState('revealed');
      startAutoHide();
    } catch (err) {
      setState('error');
      setErrorMsg((err as Error).message);
    }
  };

  const hide = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    onHide();
    setState('idle');
  };

  if (state === 'revealed') {
    return (
      <button
        type="button"
        onClick={hide}
        className="mt-1 inline-flex items-center rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-0.5 text-xs font-medium text-forge-700 dark:text-forge-400 hover:bg-stone-50 dark:hover:bg-stone-800 hover:underline"
        title="Auto-hides after a short delay"
      >
        Hide {label}
      </button>
    );
  }

  if (state === 'loading') {
    return (
      <p className="mt-1 text-xs text-forge-700 dark:text-forge-400">Revealing…</p>
    );
  }

  if (state === 'empty') {
    return (
      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
        No {label} stored yet.{' '}
        <button
          type="button"
          onClick={() => setState('idle')}
          className="text-forge-700 dark:text-forge-400 hover:underline"
        >
          Dismiss
        </button>
      </p>
    );
  }

  // idle | error
  return (
    <div className="mt-1 flex items-center gap-2">
      <button
        type="button"
        onClick={reveal}
        className="inline-flex items-center rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-0.5 text-xs font-medium text-forge-700 dark:text-forge-400 hover:bg-stone-50 dark:hover:bg-stone-800 hover:underline"
      >
        Reveal {label}
      </button>
      {state === 'error' && (
        <span className="text-xs text-red-600">{errorMsg}</span>
      )}
    </div>
  );
}
